import React, { useState, useEffect } from 'react';
import {
  Save,
  AlertCircle,
  CheckCircle,
  FileText,
  RefreshCw,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Shield,
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface EnvVariable {
  name: string;
  value: string;
  sensitive?: boolean;
}

interface EnvFile {
  id: string;
  label: string;
  path: string;
  type?: 'env' | 'nginx';
  exists: boolean;
  variables: EnvVariable[];
  rawContent: string;
}

const EnvConfigPage: React.FC = () => {
  const [files, setFiles] = useState<EnvFile[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [savingFileId, setSavingFileId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editedVars, setEditedVars] = useState<Record<string, EnvVariable[]>>({});
  const [rawContent, setRawContent] = useState<Record<string, string>>({});
  const [rawMode, setRawMode] = useState<Record<string, boolean>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadPanel();
  }, []);

  useEffect(() => {
    if (files.length > 0 && !selectedId) {
      setSelectedId(files[0].id);
    }
  }, [files, selectedId]);

  const loadPanel = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/env-config/panel');
      if (response.data.success && response.data.files) {
        setFiles(response.data.files);
        const varsInit: Record<string, EnvVariable[]> = {};
        const rawInit: Record<string, string> = {};
        response.data.files.forEach((f: EnvFile) => {
          varsInit[f.id] = f.variables.map((v) => ({ ...v, value: v.value || '' }));
          rawInit[f.id] = f.rawContent || '';
        });
        setEditedVars(varsInit);
        setRawContent(rawInit);
      }
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Você não tem permissão para acessar esta configuração.');
        toast.error('Acesso negado');
      } else {
        setError('Erro ao carregar configurações de ambiente.');
        toast.error('Erro ao carregar painel');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleRawMode = (id: string) => {
    setRawMode((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const updateVar = (fileId: string, index: number, field: 'name' | 'value', val: string) => {
    const arr = [...(editedVars[fileId] || [])];
    if (arr[index]) arr[index] = { ...arr[index], [field]: val };
    setEditedVars({ ...editedVars, [fileId]: arr });
  };

  const addVar = (fileId: string) => {
    const arr = [...(editedVars[fileId] || []), { name: '', value: '', sensitive: false }];
    setEditedVars({ ...editedVars, [fileId]: arr });
  };

  const removeVar = (fileId: string, index: number) => {
    const arr = (editedVars[fileId] || []).filter((_, i) => i !== index);
    setEditedVars({ ...editedVars, [fileId]: arr });
  };

  const syncRawToVars = (fileId: string) => {
    const content = rawContent[fileId] || '';
    const lines = content.split(/\r?\n/);
    const vars: EnvVariable[] = [];
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;
      const m = trimmed.match(/^([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.*)$/);
      if (m) vars.push({ name: m[1], value: m[2].trim(), sensitive: /PASSWORD|SECRET|KEY|TOKEN|PASS/i.test(m[1]) });
    });
    setEditedVars({ ...editedVars, [fileId]: vars });
  };

  const hasChanges = (file: EnvFile) => {
    const id = file.id;
    if (rawMode[id]) return (rawContent[id] || '') !== (file.rawContent || '');
    const orig = file.variables || [];
    const curr = editedVars[id] || [];
    if (orig.length !== curr.length) return true;
    return curr.some((v, i) => {
      const o = orig[i];
      return !o || o.name !== v.name || (o.value !== '••••••••' && o.value !== v.value);
    });
  };

  const handleSave = async (fileId: string) => {
    setSavingFileId(fileId);
    try {
      if (rawMode[fileId] || files.find(f => f.id === fileId)?.type === 'nginx') {
        await api.post('/api/env-config/panel', { fileId, content: rawContent[fileId] || '' });
      } else {
        const vars = (editedVars[fileId] || []).filter((v) => v.name.trim());
        await api.post('/api/env-config/panel', { fileId, variables: vars });
      }
      toast.success('Salvo com sucesso! Reinicie o servidor se necessário.');
      await loadPanel();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSavingFileId(null);
    }
  };

  const handleNginxTest = async () => {
    try {
      const response = await api.post('/api/env-config/nginx/test');
      toast.success(response.data.message);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao testar Nginx');
      console.error(err);
    }
  };

  const handleNginxReload = async () => {
    try {
      const response = await api.post('/api/env-config/nginx/reload');
      toast.success(response.data.message);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao recarregar Nginx');
      console.error(err);
    }
  };

  if (loading && files.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando painel...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertCircle className="h-12 w-12 mb-2" />
        <p className="text-lg font-medium">{error}</p>
        <button onClick={loadPanel} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          Tentar Novamente
        </button>
      </div>
    );
  }

  const activeFile = files.find((f) => f.id === selectedId);
  const isNginx = activeFile?.type === 'nginx';
  const isRaw = activeFile ? (isNginx || (rawMode[activeFile.id] ?? false)) : false;
  const vars = activeFile ? (editedVars[activeFile.id] ?? []) : [];
  const content = activeFile ? (rawContent[activeFile.id] ?? '') : '';

  return (
    <div className="space-y-4">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-5 w-5 text-amber-500" />
            <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded">
              Apenas administradores
            </span>
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
            Configuração de Ambiente
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Selecione o arquivo no menu e edite as variáveis
          </p>
        </div>
        <div className="flex gap-2">
          {isNginx && (
            <>
              <button
                onClick={handleNginxTest}
                className="px-4 py-2 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors text-sm flex items-center gap-2 shrink-0"
              >
                <CheckCircle className="h-4 w-4" />
                Testar Config
              </button>
              <button
                onClick={handleNginxReload}
                className="px-4 py-2 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors text-sm flex items-center gap-2 shrink-0"
              >
                <RefreshCw className="h-4 w-4" />
                Reload Nginx
              </button>
            </>
          )}
          <button
            onClick={loadPanel}
            disabled={loading}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm flex items-center gap-2 shrink-0"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Recarregar
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 min-h-[500px]">
        {/* Menu vertical à esquerda */}
        <nav className="w-full lg:w-56 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Arquivos ENV
            </span>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50 max-h-[400px] lg:max-h-none overflow-y-auto">
            {files.map((file) => {
              const changed = hasChanges(file);
              const isActive = selectedId === file.id;
              return (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => setSelectedId(file.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                    isActive
                      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {file.exists ? (
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  )}
                  <span className="truncate flex-1">{file.label}</span>
                  {changed && (
                    <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" title="Alterações não salvas" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Conteúdo à direita */}
        <div className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
          {activeFile ? (
            <>
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{activeFile.label}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate hidden sm:inline">
                    {activeFile.path}
                  </span>
                  {!activeFile.exists && (
                    <span className="text-xs text-amber-600 dark:text-amber-400">(será criado ao salvar)</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!isNginx && (
                    <button
                      type="button"
                      onClick={() => toggleRawMode(activeFile.id)}
                      className="px-3 py-1.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center gap-1"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {isRaw ? 'Formulário' : 'Raw'}
                    </button>
                  )}
                  {!isRaw && (
                    <button
                      type="button"
                      onClick={() => addVar(activeFile.id)}
                      className="px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded flex items-center gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Variável
                    </button>
                  )}
                  {hasChanges(activeFile) && (
                    <button
                      type="button"
                      onClick={() => handleSave(activeFile.id)}
                      disabled={savingFileId === activeFile.id}
                      className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 flex items-center gap-1"
                    >
                      {savingFileId === activeFile.id ? (
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Save className="h-3.5 w-3.5" />
                      )}
                      Salvar
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 p-4 overflow-auto">
                {isRaw ? (
                  <textarea
                    value={content}
                    onChange={(e) => setRawContent({ ...rawContent, [activeFile.id]: e.target.value })}
                    onBlur={() => syncRawToVars(activeFile.id)}
                    className="w-full min-h-[400px] p-4 font-mono text-sm bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder={isNginx ? "# Configuração do Nginx" : "# Variáveis de ambiente"}
                    spellCheck={false}
                  />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Variável
                          </th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Valor
                          </th>
                          <th className="w-10" />
                        </tr>
                      </thead>
                      <tbody>
                        {vars.map((v, i) => (
                          <tr key={i} className="border-b border-gray-100 dark:border-gray-700/50">
                            <td className="py-2 px-3">
                              <input
                                value={v.name}
                                onChange={(e) => updateVar(activeFile.id, i, 'name', e.target.value)}
                                className="w-full bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono"
                                placeholder="NOME_DA_VARIAVEL"
                              />
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type={
                                    v.sensitive && !showSecrets[`${activeFile.id}-${i}`] ? 'password' : 'text'
                                  }
                                  value={v.value}
                                  onChange={(e) => updateVar(activeFile.id, i, 'value', e.target.value)}
                                  className="flex-1 bg-transparent border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm font-mono"
                                  placeholder="valor"
                                />
                                {v.sensitive && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowSecrets((s) => ({
                                        ...s,
                                        [`${activeFile.id}-${i}`]: !s[`${activeFile.id}-${i}`],
                                      }))
                                    }
                                    className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                    title={showSecrets[`${activeFile.id}-${i}`] ? 'Ocultar' : 'Mostrar'}
                                  >
                                    {showSecrets[`${activeFile.id}-${i}`] ? (
                                      <EyeOff className="h-4 w-4" />
                                    ) : (
                                      <Eye className="h-4 w-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => removeVar(activeFile.id, i)}
                                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {vars.length === 0 && (
                      <p className="py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                        Nenhuma variável. Clique em &quot;Variável&quot; ou mude para o modo Raw para adicionar.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm p-8">
              Selecione um arquivo no menu ao lado
            </div>
          )}
        </div>
      </div>

      {files.some((f) => hasChanges(f)) && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            Há alterações não salvas. Selecione o arquivo e clique em Salvar.
          </p>
        </div>
      )}

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">Informações</h3>
        <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
          <li>Backup automático é criado antes de cada alteração</li>
          <li>Variáveis sensíveis são mascaradas – deixe em branco para manter o valor atual</li>
          <li>Arquivos vazios serão criados ao salvar pela primeira vez</li>
          <li>Reinicie o servidor após salvar para aplicar as mudanças</li>
        </ul>
      </div>
    </div>
  );
};

export default EnvConfigPage;
